import { useEffect, useRef, useState } from 'react';

import { useMobil } from '../mobil-context.ts';

/**
 * Aláírás ujjal vagy egérrel. (3.0 / 5.1)
 *
 * Sima böngészős rajzfelület — nem kell hozzá külön program. A sofőr a
 * saját aláírását egyszer állítja be a profiljában, a partnerét pedig a
 * helyszínen rajzolja ide.
 */
export function AlairasMezo({
  cimke,
  kezdoKep,
  magassag,
  onValtozott,
}: {
  cimke: string;
  /** Meglévő aláírás (adat-URL), ha van. */
  kezdoKep?: string | null;
  /** Alapból 120 képpont, telefonon 200 — ott ujjal írnak alá. (3.0 / 8.) */
  magassag?: number;
  /** A rajz minden mozdulat után: adat-URL vagy null, ha üres. */
  onValtozott: (kep: string | null) => void;
}) {
  const mobil = useMobil();
  // A vászon magasságát a rajzoló állítja be, ezért nem elég a stíluslap:
  // telefonon itt kell magasabbra venni, hogy ujjal is elférjen az aláírás.
  const magas = magassag ?? (mobil ? 200 : 120);
  const vaszon = useRef<HTMLCanvasElement>(null);
  const [rajzol, setRajzol] = useState(false);
  const [vanRajz, setVanRajz] = useState(!!kezdoKep);

  /**
   * A rajz befoglaló téglalapja. A mentett kép csak ezt a részt
   * tartalmazza, nem az egész vásznat.
   *
   * Az okmányon az aláírás **magasság szerint** van méretezve (24–34
   * képpont). Ha a kép fele üres, a vonalak ugyanennyivel kisebbek
   * lesznek — két ugyanakkora aláírás más méretben jelenne meg aszerint,
   * hogy a vászon melyik részére írták. A vágással ez megszűnik.
   * (2026-10-04)
   */
  const hatar = useRef<{ x1: number; y1: number; x2: number; y2: number } | null>(null);

  function hatarBovit(x: number, y: number): void {
    const h = hatar.current;
    hatar.current = h
      ? { x1: Math.min(h.x1, x), y1: Math.min(h.y1, y), x2: Math.max(h.x2, x), y2: Math.max(h.y2, y) }
      : { x1: x, y1: y, x2: x, y2: y };
  }

  /** A vászon tartalma a rajz köré vágva, 6 képpont levegővel. */
  function vagottKep(): string | null {
    const c = vaszon.current;
    const h = hatar.current;
    if (!c) return null;
    if (!h) return c.toDataURL('image/png');

    const keret = 6;
    const x = Math.max(0, Math.floor(h.x1 - keret));
    const y = Math.max(0, Math.floor(h.y1 - keret));
    const sz = Math.min(c.width - x, Math.ceil(h.x2 - h.x1 + keret * 2));
    const ma = Math.min(c.height - y, Math.ceil(h.y2 - h.y1 + keret * 2));
    if (sz < 2 || ma < 2) return c.toDataURL('image/png');

    const ki = document.createElement('canvas');
    ki.width = sz;
    ki.height = ma;
    const ctx = ki.getContext('2d');
    if (!ctx) return c.toDataURL('image/png');
    ctx.drawImage(c, x, y, sz, ma, 0, 0, sz, ma);
    return ki.toDataURL('image/png');
  }

  useEffect(() => {
    const c = vaszon.current;
    if (!c) return;
    // A rajzfelület pontos mérete csak megjelenés után ismert.
    c.width = c.clientWidth;
    c.height = magas;
    const ctx = c.getContext('2d');
    if (!ctx) return;
    ctx.lineWidth = 2;
    ctx.lineCap = 'round';
    ctx.strokeStyle = '#14171A';
    hatar.current = null;
    if (kezdoKep) {
      const kep = new Image();
      kep.onload = () => {
        ctx.drawImage(kep, 0, 0);
        /* A meglévő aláírás is a rajz része: ha ráírnak, azt se vágjuk le. */
        hatarBovit(0, 0);
        hatarBovit(kep.width, kep.height);
      };
      kep.src = kezdoKep;
    }
  }, [kezdoKep, magas]);

  function pont(e: React.PointerEvent<HTMLCanvasElement>) {
    const c = vaszon.current!;
    const r = c.getBoundingClientRect();
    return { x: e.clientX - r.left, y: e.clientY - r.top };
  }

  function kezd(e: React.PointerEvent<HTMLCanvasElement>) {
    const ctx = vaszon.current?.getContext('2d');
    if (!ctx) return;
    vaszon.current?.setPointerCapture(e.pointerId);
    const p = pont(e);
    ctx.beginPath();
    ctx.moveTo(p.x, p.y);
    hatarBovit(p.x, p.y);
    setRajzol(true);
  }

  function huz(e: React.PointerEvent<HTMLCanvasElement>) {
    if (!rajzol) return;
    const ctx = vaszon.current?.getContext('2d');
    if (!ctx) return;
    const p = pont(e);
    ctx.lineTo(p.x, p.y);
    ctx.stroke();
    hatarBovit(p.x, p.y);
    setVanRajz(true);
  }

  function veg() {
    if (!rajzol) return;
    setRajzol(false);
    onValtozott(vanRajz ? vagottKep() : null);
  }

  function torol() {
    const c = vaszon.current;
    const ctx = c?.getContext('2d');
    if (!c || !ctx) return;
    ctx.clearRect(0, 0, c.width, c.height);
    hatar.current = null;
    setVanRajz(false);
    onValtozott(null);
  }

  return (
    <div className="alairas">
      <div className="alfej">
        <span>{cimke}</span>
        <button className="btn sm" type="button" onClick={torol}>
          Törlés
        </button>
      </div>
      <canvas
        ref={vaszon}
        style={{ height: magas }}
        onPointerDown={kezd}
        onPointerMove={huz}
        onPointerUp={veg}
        onPointerLeave={veg}
      />
      <div className="alvonal">Aláírás</div>
    </div>
  );
}
